import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AnimatedSection } from '@/components/shared/AnimatedSection'
import { getImageUrl } from '@/lib/image-url'

// Real HBNV room definitions — matches hbnuevavalladolid.com/habitaciones exactly
const ROOMS = [
  {
    slug: 'deluxe-queen',
    translationKey: 'deluxe_queen',
    heroImage: '/images/nuva/rooms/room-deluxe-queen-hero.jpeg',
    name: 'Deluxe Queen',
    capacity: '2 personas - Baño privado',
  },
  {
    slug: 'deluxe-queen-balcony',
    translationKey: 'deluxe_queen_balcony',
    heroImage: '/images/nuva/rooms/room-deluxe-queen-balcony-hero.jpeg',
    name: 'Deluxe Queen Balcony',
    capacity: '2 personas - Baño privado',
  },
  {
    slug: 'junior-suite-king',
    translationKey: 'junior_suite_king',
    heroImage: '/images/nuva/rooms/room-junior-suite-king-hero.jpeg',
    name: 'Junior Suite King',
    capacity: '2 personas - Baño privado',
  },
  {
    slug: 'doble-queen',
    translationKey: 'doble_queen',
    heroImage: '/images/nuva/rooms/room-doble-queen-hero.jpeg',
    name: 'Doble Queen',
    capacity: '4 personas - Baño privado',
  },
  {
    slug: 'master-suite',
    translationKey: 'master_suite',
    heroImage: '/images/nuva/rooms/room-master-suite-hero.jpeg',
    name: 'Master Suite',
    capacity: '4 personas - Baño privado',
  },
]

export function RoomsPreview() {
  const { t, i18n } = useTranslation()
  const es = i18n.language.startsWith('es')

  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-10">
        {/* Section header — matches real site: heading + subheading centered */}
        <AnimatedSection className="mb-10 text-center">
          <h2
            className="text-3xl font-bold uppercase tracking-wide"
            style={{ color: '#0F2966' }}
          >
            {es ? 'Habitaciones' : 'Rooms'}
          </h2>
          <p className="mt-2 text-base" style={{ color: '#666666' }}>
            {es
              ? 'Habitaciones Diseñadas para tu Comodidad'
              : 'Rooms Designed for Your Comfort'}
          </p>
        </AnimatedSection>

        {/* 3-column photo grid — matches real site gallery layout */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ROOMS.map((room, index) => (
            <motion.div
              key={room.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: index * 0.08, ease: 'easeOut' }}
              className="group relative overflow-hidden shadow-sm"
              style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.14)' }}
            >
              {/* Full-bleed card image — real site uses padding-top:326px tall portrait cards */}
              <div className="relative" style={{ paddingTop: '120%' }}>
                <img
                  src={getImageUrl(room.heroImage)}
                  alt={t(`rooms.types.${room.translationKey}.name`)}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Dark gradient overlay at bottom for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                {/* Text overlay at bottom — matches real site: title + capacity + CTA */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3
                    className="text-lg font-semibold text-white uppercase tracking-wide"
                    style={{ fontSize: '23px', fontWeight: 600 }}
                  >
                    {t(`rooms.types.${room.translationKey}.name`)}
                  </h3>
                  <p className="mt-1 text-sm text-white/80">{room.capacity}</p>

                  {/* VER HABITACIÓN pill button — outline style on dark bg */}
                  <Link
                    to={`/rooms/${room.slug}`}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-3 inline-flex items-center justify-center border border-white text-white text-xs font-bold uppercase tracking-widest transition-colors hover:bg-white/10"
                    style={{
                      borderRadius: '100px',
                      width: '175px',
                      height: '40px',
                      fontSize: '12px',
                    }}
                  >
                    {es ? 'VER HABITACIÓN' : 'VIEW ROOM'}
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* "Ver todas las habitaciones" CTA — matches real site's view all link */}
        <AnimatedSection className="mt-12 text-center">
          <Link
            to="/rooms"
            className="inline-flex items-center justify-center border font-bold uppercase tracking-widest text-white transition-colors hover:bg-cream"
            style={{
              backgroundColor: '#0F2966',
              borderColor: '#0F2966',
              borderRadius: '100px',
              width: '200px',
              height: '50px',
              fontSize: '14px',
              color: '#FFFFFF',
            }}
          >
            {es ? 'Ver Habitaciones' : 'View Rooms'}
          </Link>
        </AnimatedSection>
      </div>
    </section>
  )
}
